/**
 * The top of the workspace: which automation this is, and the one switch (D9).
 *
 * IT STAYS PUT WHILE THE TABS CHANGE. Name, category and description say what
 * the automation IS, which the reader needs on every tab; the switch says
 * whether it runs, which they may want to change from any of them. So both sit
 * above the tab bar and never inside a tab.
 *
 * THE SWITCH IS THE SAME COMPONENT, MOVED, NOT REBUILT. `AutomationEnabledToggle`
 * keeps everything A5b gave it — capability gate, pessimistic write, one request
 * per intent (including two clicks in one tick), the server's answer deciding
 * the switch and the toast. This file only decides where it stands. A type with
 * no on/off (the reminder) renders no switch, exactly as before.
 *
 * THE STATUS WORD STANDS BESIDE THE SWITCH. A switch alone says "on" or "off" as
 * a shape; the pill next to it says it as a word — "Aktiv", "Aus", "Manuell",
 * "In Vorbereitung" (PC4) — so the state is readable without relying on the switch's position or colour.
 * Both come from `automationStatusPresentation`, the one rule.
 *
 * THE TECHNICAL KEY IS STILL NOT RENDERED (U13): it addresses requests, it is
 * not a line of the page.
 */
import React from "react";

import AutomationEnabledToggle from "./AutomationEnabledToggle";
import AutomationStatusPill from "./AutomationStatusPill";
import { AutomationIconTile, automationCategoryIcon } from "./automationIcons";
import { automationCategoryLabel } from "./automationLabels";

export default function AutomationWorkspaceHeader({ automation, onUpdated }) {
  if (!automation) return null;

  return (
    <header className="flex flex-wrap items-start gap-x-4 gap-y-3">
      <div className="flex min-w-0 flex-1 basis-[240px] items-start gap-3">
        <AutomationIconTile
          icon={automationCategoryIcon(automation.category)}
          tone="neutral"
        />
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="m-0 break-words text-[16px] font-semibold leading-tight text-foreground">
            {automation.label}
          </h2>
          <p className="m-0 text-[11.5px] font-medium uppercase tracking-wide text-muted-foreground">
            {automationCategoryLabel(automation.category)}
          </p>
          <p className="m-0 break-words text-[12.5px] leading-relaxed text-muted-foreground">
            {automation.description}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2.5">
        <AutomationStatusPill automation={automation} />
        <AutomationEnabledToggle automation={automation} onUpdated={onUpdated} />
      </div>
    </header>
  );
}
